import { useEffect, useMemo, useRef, useState } from "react"
import { ArrowUpDown, ChevronDown, ChevronUp, Search } from "lucide-react"
import { Popover } from "@mui/material"
import { getUniqueOptions, applyColumnFilters } from "./tableFilters"

function SelectAllCheckbox({ checked, indeterminate, onChange }) {
  const ref = useRef(null)
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate
  }, [indeterminate])
  return <input ref={ref} type="checkbox" checked={checked} onChange={onChange} />
}

function ColumnFilterPopover({ open, anchorEl, onClose, label, options, selected, onApply, onClear }) {
  const [search, setSearch] = useState("")
  const [draft, setDraft] = useState(() => new Set(selected ?? options.map((o) => o.value)))

  useEffect(() => {
    if (open) {
      setDraft(new Set(selected ?? options.map((o) => o.value)))
      setSearch("")
    }
    // Only reset the draft when the popover transitions open; while it's
    // open the user's in-progress selection should not be clobbered.

  }, [open])

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options
    const q = search.trim().toLowerCase()
    return options.filter((o) => o.label.toLowerCase().includes(q))
  }, [options, search])

  const allChecked = options.length > 0 && draft.size === options.length
  const noneChecked = draft.size === 0
  const isIndeterminate = !allChecked && !noneChecked

  const toggleValue = (value) => {
    setDraft((prev) => {
      const next = new Set(prev)
      if (next.has(value)) next.delete(value)
      else next.add(value)
      return next
    })
  }

  const toggleSelectAll = () => {
    setDraft(allChecked ? new Set() : new Set(options.map((o) => o.value)))
  }

  const handleApply = () => {
    onApply(draft.size === options.length ? null : new Set(draft))
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      disablePortal={false}
      container={typeof document !== "undefined" ? document.body : undefined}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 10 }}
      PaperProps={{ className: "column-filter-popover", sx: { backgroundColor: "#fff", backgroundImage: "none", opacity: 1 } }}
    >
      <div className="column-filter-popover__search">
        <Search size={12} />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search values..."
          autoFocus
        />
      </div>
      <label className="column-filter-popover__select-all">
        <SelectAllCheckbox
          checked={allChecked}
          indeterminate={isIndeterminate}
          onChange={toggleSelectAll}
        />
        Select All
      </label>
      <div className="column-filter-popover__list">
        {filteredOptions.length === 0 && (
          <div className="column-filter-popover__empty">No values</div>
        )}
        {filteredOptions.map((opt) => (
          <label key={opt.value} className="column-filter-popover__option">
            <input
              type="checkbox"
              checked={draft.has(opt.value)}
              onChange={() => toggleValue(opt.value)}
            />
            <span className="column-filter-popover__option-label">{opt.label}</span>
            <span className="column-filter-popover__option-count">{opt.count}</span>
          </label>
        ))}
      </div>
      <div className="column-filter-popover__actions">
        <button type="button" className="column-filter-popover__btn" onClick={onClear}>
          Clear
        </button>
        <button
          type="button"
          className="column-filter-popover__btn column-filter-popover__btn--primary"
          onClick={handleApply}
        >
          Apply
        </button>
      </div>
    </Popover>
  )
}

// Renders a sortable column header label plus a small Excel-style filter
// dropdown (checkbox list of unique values). Replaces the old permanent
// filter-row inputs: the filter UI only appears in a popover on click.
export default function FilterableHeaderCell({
  label,
  filterKey,
  sortable = true,
  sortConfig,
  onToggleSort,
  rows,
  filters,
  onApplyFilter,
}) {
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const selected = filters[filterKey]
  const isActive = selected !== undefined

  const options = useMemo(() => {
    if (!open) return []
    return getUniqueOptions(applyColumnFilters(rows, filters, filterKey), filterKey)
  }, [open, rows, filters, filterKey])

  const isSorted = sortable && sortConfig.key === filterKey

  return (
    <span className="header-cell-filterable">
      {sortable ? (
        <button
          type="button"
          className={`sortable ${isSorted ? `is-${sortConfig.direction}` : ""}`}
          onClick={() => onToggleSort(filterKey)}
        >
          <span>{label}</span>
          {!isSorted ? (
            <ArrowUpDown size={12} />
          ) : sortConfig.direction === "asc" ? (
            <ChevronUp size={12} />
          ) : (
            <ChevronDown size={12} />
          )}
        </button>
      ) : (
        <span className="header-cell-filterable__label">{label}</span>
      )}
      <button
        type="button"
        className={`header-filter-btn ${isActive ? "is-active" : ""}`}
        onClick={(e) => {
          e.stopPropagation()
          setAnchorEl(e.currentTarget)
        }}
        aria-label={`Filter ${label}`}
        title={`Filter ${label}`}
      >
        <ChevronDown size={11} />
      </button>
      <ColumnFilterPopover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        label={label}
        options={options}
        selected={selected}
        onApply={(next) => {
          onApplyFilter(filterKey, next)
          setAnchorEl(null)
        }}
        onClear={() => {
          onApplyFilter(filterKey, null)
          setAnchorEl(null)
        }}
      />
    </span>
  )
}
